// ═══════════════════════════════════════════════════════════════
// FEMA Reimbursement Tracker — Project Worksheets + Submission Log
// Tracks PW formulation status and FEMA submission versions
// ═══════════════════════════════════════════════════════════════

import React, { useEffect, useState, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select,
  Textarea, ActionIcon, Modal, Stack, SimpleGrid, MultiSelect,
} from '../../components/gab/GabComponents';
import {
  IconPlus, IconTrash, IconPencil, IconChevronDown, IconChevronUp,
  IconEye, IconClipboardList, IconCheck, IconX,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useTampaDebrisStore } from '../../store/tampaDebrisStore';
import { useDebrisOpsStore } from '../../store/debrisOpsStore';
import { useDebrisStore } from '../../store/debris-store';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import type { FEMAPackageStatus } from '../../types/debris';
import { formatCurrency } from '../../lib/formatUtils';

// ── PW meta embedded in notes ────────────────────────────────
interface PWMeta {
  type: 'pw';
  workCategory: 'Cat A' | 'Cat B' | 'Cat Z';
  description: string;
  estimatedAmount: number;
  obligatedAmount: number | null;
  linkedInvoiceIds: number[];
  linkedReconciliationIds: number[];
  portalRef: string;
}

// ── Submission meta embedded in notes ───────────────────────
interface SubMeta {
  type: 'submission';
  version: number;
  submittedDate: string;
  submittedBy: string;
  pwIds: number[];
  portalRef: string;
  changeNotes: string;
}

// ── PW status config (user-facing labels) ───────────────────
const PW_STATUS_LABELS: Record<FEMAPackageStatus, string> = {
  draft: 'Formulating',
  ready_for_review: 'Ready',
  submitted_to_fema: 'Submitted',
  approved: 'Obligated',
  amended: 'Closed',
};

const PW_STATUS_COLORS: Record<FEMAPackageStatus, string> = {
  draft: '#939598',
  ready_for_review: '#3b82f6',
  submitted_to_fema: '#C68700',
  approved: 'var(--color-success)',
  amended: '#64748b',
};

const PW_STATUS_TO_FEMA: Record<string, FEMAPackageStatus> = {
  Formulating: 'draft',
  Ready: 'ready_for_review',
  Submitted: 'submitted_to_fema',
  Obligated: 'approved',
  Closed: 'amended',
};

const FEMA_TO_PW_STATUS: Record<FEMAPackageStatus, string> = {
  draft: 'Formulating',
  ready_for_review: 'Ready',
  submitted_to_fema: 'Submitted',
  approved: 'Obligated',
  amended: 'Closed',
};

// ── Regex markers ────────────────────────────────────────────
const PWMETA_RE = /\|\|\|PWMETA\|\|\|([\s\S]*?)\|\|\|ENDPWMETA\|\|\|/;
const SUBMETA_RE = /\|\|\|SUBMETA\|\|\|([\s\S]*?)\|\|\|ENDSUBMETA\|\|\|/;
export const PWMETA_DETECT = /\|\|\|PWMETA\|\|\|/;
export const SUBMETA_DETECT = /\|\|\|SUBMETA\|\|\|/;

function decodePWMeta(notes: string): PWMeta | null {
  const m = notes?.match(PWMETA_RE);
  if (!m) return null;
  try { return JSON.parse(m[1]); } catch { return null; }
}

function decodeSubMeta(notes: string): SubMeta | null {
  const m = notes?.match(SUBMETA_RE);
  if (!m) return null;
  try { return JSON.parse(m[1]); } catch { return null; }
}

function fmtInputNum(v: string | number | undefined) {
  if (v === undefined || v === '' || v === null) return '';
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  if (isNaN(n)) return String(v);
  return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}
const stripNumFmt = (s: string) => s.replace(/[^0-9.]/g, '');

// ── Readiness check ─────────────────────────────────────────
interface ReadinessCheck {
  label: string;
  passed: boolean;
  note: string;
}

function computeReadiness(
  meta: PWMeta,
  invoiceMap: Map<number, { amount: number; status: string }>,
): { checks: ReadinessCheck[]; score: number } {
  const checks: ReadinessCheck[] = [
    {
      label: 'At least one invoice linked',
      passed: meta.linkedInvoiceIds.length > 0,
      note: meta.linkedInvoiceIds.length > 0
        ? `${meta.linkedInvoiceIds.length} invoice(s) linked`
        : 'No invoices linked',
    },
    {
      label: 'All linked invoices have amounts > $0',
      passed:
        meta.linkedInvoiceIds.length > 0 &&
        meta.linkedInvoiceIds.every(id => (invoiceMap.get(id)?.amount ?? 0) > 0),
      note:
        meta.linkedInvoiceIds.length === 0
          ? 'No invoices to check'
          : meta.linkedInvoiceIds.every(id => (invoiceMap.get(id)?.amount ?? 0) > 0)
          ? 'All invoices have amounts'
          : 'One or more invoices have $0 amount',
    },
    {
      label: 'At least one reconciliation linked',
      passed: meta.linkedReconciliationIds.length > 0,
      note: meta.linkedReconciliationIds.length > 0
        ? `${meta.linkedReconciliationIds.length} reconciliation(s) linked`
        : 'No reconciliations linked',
    },
    {
      label: 'Estimated amount is set',
      passed: (meta.estimatedAmount ?? 0) > 0,
      note: (meta.estimatedAmount ?? 0) > 0
        ? `Estimated: ${formatCurrency(meta.estimatedAmount)}`
        : 'Estimated amount is $0',
    },
    {
      label: 'PA Grants Portal project # documented',
      passed: (meta.portalRef ?? '') !== '',
      note: (meta.portalRef ?? '') !== ''
        ? `Project #: ${meta.portalRef}`
        : 'No PA Grants Portal project # on file',
    },
  ];
  const score = checks.filter(c => c.passed).length;
  return { checks, score };
}

function ReadinessBadge({ score }: { score: number }) {
  const color = score === 5 ? 'var(--color-success)' : score >= 3 ? '#C68700' : '#ef4444';
  const label = score === 5 ? 'Ready' : score >= 3 ? 'Almost Ready' : 'Not Ready';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 8px', borderRadius: 12,
      background: color + '18', color,
      fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
    }}>
      {score}/5 {label}
    </span>
  );
}

// ── Empty forms ──────────────────────────────────────────────
interface PWForm {
  pwNumber: string;
  workCategory: 'Cat A' | 'Cat B' | 'Cat Z';
  description: string;
  estimatedAmount: string;
  obligatedAmount: string;
  linkedInvoiceIds: string[];
  linkedReconciliationIds: string[];
  portalRef: string;
  status: string;
}

const EMPTY_PW: PWForm = {
  pwNumber: '',
  workCategory: 'Cat A',
  description: '',
  estimatedAmount: '',
  obligatedAmount: '',
  linkedInvoiceIds: [],
  linkedReconciliationIds: [],
  portalRef: '',
  status: 'Formulating',
};

interface SubForm {
  selectedPwIds: string[];
  submittedDate: string;
  submittedBy: string;
  portalRef: string;
  changeNotes: string;
}

const todayStr = () => new Date().toISOString().slice(0, 10);

// ── Main Component ───────────────────────────────────────────
export default function PWRegister() {
  const { activeIncident, user } = useAuthStore();
  const { canEdit } = useModulePermission('debris');
  const incidentId = activeIncident?.id?.toString() ?? '';

  const {
    femaPackages, loadingFemaPackages, loadFemaPackages,
    createFemaPackage, updateFemaPackage, deleteFemaPackage,
    reconciliations, loadReconciliations,
  } = useTampaDebrisStore();

  const { invoices, loadInvoices } = useDebrisOpsStore();
  const { contractors } = useDebrisStore();

  // Load data on mount
  useEffect(() => {
    loadFemaPackages();
    loadReconciliations();
    loadInvoices();
  }, [incidentId]);

  // Partition femaPackages into PWs, submissions, and regular
  const pwRecords = useMemo(
    () => femaPackages.filter(p => PWMETA_DETECT.test(p.notes ?? '')),
    [femaPackages],
  );
  const submissionRecords = useMemo(
    () => femaPackages.filter(p => SUBMETA_DETECT.test(p.notes ?? '')),
    [femaPackages],
  );

  // Invoice lookup map: id → { amount, status }
  const invoiceMap = useMemo(() => {
    const m = new Map<number, { amount: number; status: string; invoiceNumber: string; contractor: string }>();
    invoices.forEach(inv => {
      m.set(inv.id, {
        amount: parseFloat(inv.totalAmount) || 0,
        status: inv.status,
        invoiceNumber: inv.invoiceNumber,
        contractor: inv.contractorId,
      });
    });
    return m;
  }, [invoices]);

  // Reconciliation lookup map
  const reconMap = useMemo(() => {
    const m = new Map<number, { contractor: string; periodEnd: string; status: string; amount: number }>();
    reconciliations.forEach(r => {
      m.set(r.id, {
        contractor: r.contractorId,
        periodEnd: r.periodEnd,
        status: r.status,
        amount: r.approvedAmount || r.totalVerified,
      });
    });
    return m;
  }, [reconciliations]);

  // ── PW Sort ──────────────────────────────────────────────
  const [pwSortField, setPwSortField] = useState('');
  const [pwSortDir, setPwSortDir] = useState<'asc' | 'desc'>('asc');
  const togglePwSort = (field: string) => {
    if (pwSortField === field) setPwSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setPwSortField(field); setPwSortDir('asc'); }
  };

  const sortedPWs = useMemo(() => {
    const list = [...pwRecords];
    if (!pwSortField) return list.sort((a, b) => (b.id || 0) - (a.id || 0));
    return list.sort((a, b) => {
      const va = (a as any)[pwSortField] ?? '';
      const vb = (b as any)[pwSortField] ?? '';
      const n = String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
      return pwSortDir === 'asc' ? n : -n;
    });
  }, [pwRecords, pwSortField, pwSortDir]);

  // ── Sub Sort ─────────────────────────────────────────────
  const [subSortField, setSubSortField] = useState('');
  const [subSortDir, setSubSortDir] = useState<'asc' | 'desc'>('asc');
  const toggleSubSort = (field: string) => {
    if (subSortField === field) setSubSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSubSortField(field); setSubSortDir('asc'); }
  };

  const sortedSubs = useMemo(() => {
    const list = [...submissionRecords];
    if (!subSortField) return list.sort((a, b) => (b.id || 0) - (a.id || 0));
    return list.sort((a, b) => {
      const metaA = decodeSubMeta(a.notes ?? '');
      const metaB = decodeSubMeta(b.notes ?? '');
      let va: string | number = '';
      let vb: string | number = '';
      switch (subSortField) {
        case 'version': va = metaA?.version ?? (a as any).version ?? ''; vb = metaB?.version ?? (b as any).version ?? ''; break;
        case 'submittedDate': va = metaA?.submittedDate ?? ''; vb = metaB?.submittedDate ?? ''; break;
        case 'submittedBy': va = metaA?.submittedBy ?? ''; vb = metaB?.submittedBy ?? ''; break;
        case 'pwCount': va = metaA?.pwIds?.length ?? 0; vb = metaB?.pwIds?.length ?? 0; break;
        case 'portalRef': va = metaA?.portalRef ?? ''; vb = metaB?.portalRef ?? ''; break;
        case 'changeNotes': va = metaA?.changeNotes ?? ''; vb = metaB?.changeNotes ?? ''; break;
        default: va = (a as any)[subSortField] ?? ''; vb = (b as any)[subSortField] ?? '';
      }
      const n = String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
      return subSortDir === 'asc' ? n : -n;
    });
  }, [submissionRecords, subSortField, subSortDir]);

  // ── PW summary stats ──────────────────────────────────────
  const summaryStats = useMemo(() => {
    let totalEstimated = 0;
    let totalObligated = 0;
    let readyCount = 0;
    pwRecords.forEach(p => {
      const meta = decodePWMeta(p.notes ?? '');
      if (!meta) return;
      totalEstimated += meta.estimatedAmount || 0;
      if (meta.obligatedAmount != null) totalObligated += meta.obligatedAmount;
      const { score } = computeReadiness(meta, invoiceMap as any);
      if (score === 5) readyCount++;
    });
    return { totalEstimated, totalObligated, readyCount };
  }, [pwRecords, invoiceMap]);

  // ── Expanded row ──────────────────────────────────────────
  const [expandedId, setExpandedId] = useState<number | null>(null);

  // ── PW Modal ──────────────────────────────────────────────
  const [pwModalOpen, setPwModalOpen] = useState(false);
  const [editTargetId, setEditTargetId] = useState<number | null>(null);
  const [pwForm, setPwForm] = useState<PWForm>({ ...EMPTY_PW });
  const [pwSaving, setPwSaving] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  function openAddPW() {
    setEditTargetId(null);
    setPwForm({ ...EMPTY_PW });
    setPwModalOpen(true);
  }

  function openEditPW(pkg: typeof femaPackages[0]) {
    const meta = decodePWMeta(pkg.notes ?? '');
    setEditTargetId(pkg.id);
    setPwForm({
      pwNumber: pkg.pwReference || '',
      workCategory: meta?.workCategory ?? 'Cat A',
      description: meta?.description ?? '',
      estimatedAmount: meta?.estimatedAmount != null ? String(meta.estimatedAmount) : '',
      obligatedAmount: meta?.obligatedAmount != null ? String(meta.obligatedAmount) : '',
      linkedInvoiceIds: (meta?.linkedInvoiceIds ?? []).map(String),
      linkedReconciliationIds: (meta?.linkedReconciliationIds ?? []).map(String),
      portalRef: meta?.portalRef ?? '',
      status: FEMA_TO_PW_STATUS[pkg.status] ?? 'Formulating',
    });
    setPwModalOpen(true);
  }

  async function savePW() {
    if (!pwForm.pwNumber.trim()) {
      notifications.show({ title: 'Required', message: 'PW Number is required', color: 'red' });
      return;
    }
    setPwSaving(true);
    try {
      const meta: PWMeta = {
        type: 'pw',
        workCategory: pwForm.workCategory,
        description: pwForm.description,
        estimatedAmount: parseFloat(pwForm.estimatedAmount) || 0,
        obligatedAmount: pwForm.obligatedAmount !== '' ? parseFloat(pwForm.obligatedAmount) || 0 : null,
        linkedInvoiceIds: pwForm.linkedInvoiceIds.map(Number),
        linkedReconciliationIds: pwForm.linkedReconciliationIds.map(Number),
        portalRef: pwForm.portalRef,
      };
      const desc = pwForm.description.slice(0, 60);
      const payload = {
        packageName: `${pwForm.pwNumber} — ${desc}`,
        femaDisasterNumber: '',
        pwReference: pwForm.pwNumber,
        notes: `|||PWMETA|||${JSON.stringify(meta)}|||ENDPWMETA|||`,
        status: PW_STATUS_TO_FEMA[pwForm.status] ?? 'draft',
        incidentId,
        dateRangeStart: '',
        dateRangeEnd: '',
        contractorIds: '[]',
        zoneIds: '[]',
        totalCubicYards: 0,
        totalCost: 0,
        version: 1,
        exportedAt: '',
        femaFeedback: '',
      };
      if (editTargetId != null) {
        await updateFemaPackage(editTargetId, payload);
        notifications.show({ title: 'Saved', message: 'Project Worksheet updated', color: 'green' });
      } else {
        await createFemaPackage(payload);
        notifications.show({ title: 'Created', message: 'Project Worksheet added', color: 'green' });
      }
      setPwModalOpen(false);
    } catch (e) {
      notifications.show({ title: 'Error', message: 'Failed to save PW', color: 'red' });
    } finally {
      setPwSaving(false);
    }
  }

  async function deletePW() {
    if (deleteTargetId == null) return;
    setDeleting(true);
    try {
      await deleteFemaPackage(deleteTargetId);
      notifications.show({ title: 'Deleted', message: 'Project Worksheet removed', color: 'green' });
      setDeleteTargetId(null);
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to delete', color: 'red' });
    } finally {
      setDeleting(false);
    }
  }

  // ── Submission Modal ──────────────────────────────────────
  const [subModalOpen, setSubModalOpen] = useState(false);
  const [viewSubId, setViewSubId] = useState<number | null>(null);
  const [deleteSubId, setDeleteSubId] = useState<number | null>(null);
  const [subForm, setSubForm] = useState<SubForm>({
    selectedPwIds: [],
    submittedDate: todayStr(),
    submittedBy: user?.email ?? '',
    portalRef: '',
    changeNotes: '',
  });
  const [subSaving, setSubSaving] = useState(false);
  const [deletingSubId, setDeletingSubId] = useState(false);

  function openLogSubmission() {
    const maxVer = submissionRecords.reduce((mx, s) => {
      const meta = decodeSubMeta(s.notes ?? '');
      return meta ? Math.max(mx, meta.version) : mx;
    }, 0);
    setSubForm({
      selectedPwIds: [],
      submittedDate: todayStr(),
      submittedBy: user?.email ?? '',
      portalRef: '',
      changeNotes: '',
    });
    setSubModalOpen(true);
  }

  async function saveSubmission() {
    if (subForm.selectedPwIds.length === 0) {
      notifications.show({ title: 'Required', message: 'Select at least one PW to include', color: 'red' });
      return;
    }
    if (!subForm.submittedDate) {
      notifications.show({ title: 'Required', message: 'Submission date is required', color: 'red' });
      return;
    }
    setSubSaving(true);
    try {
      const maxVer = submissionRecords.reduce((mx, s) => {
        const meta = decodeSubMeta(s.notes ?? '');
        return meta ? Math.max(mx, meta.version) : mx;
      }, 0);
      const version = maxVer + 1;
      const meta: SubMeta = {
        type: 'submission',
        version,
        submittedDate: subForm.submittedDate,
        submittedBy: subForm.submittedBy,
        pwIds: subForm.selectedPwIds.map(Number),
        portalRef: subForm.portalRef,
        changeNotes: subForm.changeNotes,
      };
      const payload = {
        packageName: `Submission v${version} — ${subForm.submittedDate}`,
        femaDisasterNumber: '',
        pwReference: '',
        notes: `|||SUBMETA|||${JSON.stringify(meta)}|||ENDSUBMETA|||`,
        status: 'submitted_to_fema' as FEMAPackageStatus,
        incidentId,
        version,
        dateRangeStart: '',
        dateRangeEnd: '',
        contractorIds: '[]',
        zoneIds: '[]',
        totalCubicYards: 0,
        totalCost: 0,
        exportedAt: '',
        femaFeedback: '',
      };
      await createFemaPackage(payload);
      notifications.show({ title: 'Logged', message: `Submission v${version} recorded`, color: 'green' });
      setSubModalOpen(false);
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to log submission', color: 'red' });
    } finally {
      setSubSaving(false);
    }
  }

  async function deleteSubmission() {
    if (deleteSubId == null) return;
    setDeletingSubId(true);
    try {
      await deleteFemaPackage(deleteSubId);
      notifications.show({ title: 'Deleted', message: 'Submission removed', color: 'green' });
      setDeleteSubId(null);
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to delete submission', color: 'red' });
    } finally {
      setDeletingSubId(false);
    }
  }

  // ── Invoice MultiSelect options ───────────────────────────
  const contractorLookup = useMemo(() => {
    const m = new Map<string, string>();
    contractors.forEach(c => m.set(String(c.id), c.companyName || ''));
    return m;
  }, [contractors]);
  const invoiceOptions = useMemo(() =>
    invoices
      .filter(inv => !incidentId || inv.incidentId === incidentId)
      .map(inv => {
        const cName = contractorLookup.get(inv.contractorId) || '';
        return {
          value: String(inv.id),
          label: `#${inv.invoiceNumber}${cName ? ' · ' + cName : ''} — ${formatCurrency(parseFloat(inv.totalAmount) || 0)} (${inv.status})`,
        };
      }),
    [invoices, incidentId, contractorLookup],
  );

  // ── Reconciliation MultiSelect options ────────────────────
  const reconOptions = useMemo(() =>
    reconciliations
      .filter(r => !incidentId || r.incidentId === incidentId)
      .map(r => ({
        value: String(r.id),
        label: `Recon #${r.id} — ${r.periodEnd || 'no date'} (${r.status})`,
      })),
    [reconciliations, incidentId],
  );

  // ── PW MultiSelect options for submission modal ───────────
  const pwOptions = useMemo(() =>
    pwRecords.map(p => ({
      value: String(p.id),
      label: `${p.pwReference || `PW-${p.id}`} — ${decodePWMeta(p.notes ?? '')?.description?.slice(0, 50) ?? ''}`,
    })),
    [pwRecords],
  );

  // ── Sort indicator helper ─────────────────────────────────
  function sortIndicator(field: string, currentField: string, dir: 'asc' | 'desc') {
    if (currentField !== field) return <span style={{ opacity: 0.25 }}>↑</span>;
    return dir === 'desc' ? '↓' : '↑';
  }

  const thStyle = (field: string, activeField: string): React.CSSProperties => ({
    cursor: 'pointer',
    userSelect: 'none',
    color: activeField === field ? '#4B3FFF' : '#64748b',
    padding: '10px 12px',
    textAlign: 'left',
    fontWeight: 600,
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    whiteSpace: 'nowrap',
    background: '#f8fafc',
    borderBottom: '2px solid #e2e8f0',
  });

  const tdStyle: React.CSSProperties = {
    padding: '10px 12px',
    borderBottom: '1px solid #f1f5f9',
    fontSize: 14,
    verticalAlign: 'middle',
  };

  // ── View submission detail ────────────────────────────────
  const viewedSub = viewSubId != null
    ? submissionRecords.find(s => s.id === viewSubId)
    : null;
  const viewedSubMeta = viewedSub ? decodeSubMeta(viewedSub.notes ?? '') : null;

  return (
    <div style={{ marginTop: 24 }}>
      <Group justify="space-between" mb={20}>
        <Group gap={10} align="center">
          <IconClipboardList size={24} color="#4B3FFF" />
          <div>
            <Title order={2} style={{ margin: 0 }}>Reimbursement Tracker</Title>
            <Text size="sm" c="dimmed">Project Worksheets and FEMA submission log</Text>
          </div>
        </Group>
      </Group>

      {/* ── Section 1: Project Worksheets ────────────────────── */}
      <Paper withBorder style={{ marginBottom: 24, overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0' }}>
          <Group justify="space-between" align="center">
            <div>
              <Title order={4} style={{ margin: 0 }}>Project Worksheets</Title>
              <Text size="xs" c="dimmed" mt={2}>
                One row per FEMA Project Worksheet — track formulation status and readiness
              </Text>
            </div>
            {canEdit && (
              <Button
                leftSection={<IconPlus size={16} />}
                size="sm"
                onClick={openAddPW}
              >
                Add PW
              </Button>
            )}
          </Group>
        </div>

        {/* Summary bar */}
        <div style={{ padding: '12px 20px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          <SimpleGrid cols={4} spacing={16}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#1e293b' }}>{pwRecords.length}</div>
              <div style={{ fontSize: 12, color: '#64748b' }}>Total PWs</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--color-success)' }}>{summaryStats.readyCount}</div>
              <div style={{ fontSize: 12, color: '#64748b' }}>Ready (5/5)</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#1e293b' }}>{formatCurrency(summaryStats.totalEstimated)}</div>
              <div style={{ fontSize: 12, color: '#64748b' }}>Total Estimated</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#C68700' }}>{formatCurrency(summaryStats.totalObligated)}</div>
              <div style={{ fontSize: 12, color: '#64748b' }}>Total Obligated</div>
            </div>
          </SimpleGrid>
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 420px)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: 120 }} />
              <col style={{ width: '30%' }} />
              <col style={{ width: 90 }} />
              <col style={{ width: 130 }} />
              <col style={{ width: 130 }} />
              <col style={{ width: 140 }} />
              <col style={{ width: 110 }} />
              <col style={{ width: 90 }} />
            </colgroup>
            <thead>
              <tr>
                <th style={thStyle('pwReference', pwSortField)} onClick={() => togglePwSort('pwReference')}>
                  PW # {sortIndicator('pwReference', pwSortField, pwSortDir)}
                </th>
                <th style={thStyle('packageName', pwSortField)} onClick={() => togglePwSort('packageName')}>
                  Description {sortIndicator('packageName', pwSortField, pwSortDir)}
                </th>
                <th style={{ ...thStyle('', pwSortField), cursor: 'default' }}>Category</th>
                <th style={{ ...thStyle('', pwSortField), cursor: 'default' }}>Estimated</th>
                <th style={{ ...thStyle('', pwSortField), cursor: 'default' }}>Obligated</th>
                <th style={{ ...thStyle('', pwSortField), cursor: 'default' }}>Readiness</th>
                <th style={thStyle('status', pwSortField)} onClick={() => togglePwSort('status')}>
                  Status {sortIndicator('status', pwSortField, pwSortDir)}
                </th>
                <th style={{ ...thStyle('', pwSortField), cursor: 'default' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedPWs.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ ...tdStyle, textAlign: 'center', color: '#94a3b8', padding: '40px 20px' }}>
                    No Project Worksheets yet. Add your first PW to get started.
                  </td>
                </tr>
              )}
              {sortedPWs.map(pkg => {
                const meta = decodePWMeta(pkg.notes ?? '');
                const { checks, score } = meta
                  ? computeReadiness(meta, invoiceMap as any)
                  : { checks: [], score: 0 };
                const isExpanded = expandedId === pkg.id;
                const statusColor = PW_STATUS_COLORS[pkg.status] ?? '#939598';
                const statusLabel = PW_STATUS_LABELS[pkg.status] ?? pkg.status;

                return (
                  <React.Fragment key={pkg.id}>
                    <tr
                      style={{ cursor: 'pointer', background: isExpanded ? '#f0f4ff' : 'white' }}
                      onClick={() => setExpandedId(isExpanded ? null : pkg.id)}
                    >
                      <td style={{ ...tdStyle, fontWeight: 600, color: '#4B3FFF' }}>
                        {pkg.pwReference || `PW-${pkg.id}`}
                      </td>
                      <td style={{ ...tdStyle, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {meta?.description || pkg.packageName || '—'}
                      </td>
                      <td style={tdStyle}>
                        {meta?.workCategory && (
                          <Badge size="sm" style={{ background: '#e8eaf0', color: '#374151', border: 'none' }}>
                            {meta.workCategory}
                          </Badge>
                        )}
                      </td>
                      <td style={tdStyle}>
                        {meta ? formatCurrency(meta.estimatedAmount) : '—'}
                      </td>
                      <td style={tdStyle}>
                        {meta?.obligatedAmount != null ? formatCurrency(meta.obligatedAmount) : <span style={{ color: '#94a3b8' }}>—</span>}
                      </td>
                      <td style={tdStyle}>
                        {meta ? <ReadinessBadge score={score} /> : '—'}
                      </td>
                      <td style={tdStyle}>
                        <span style={{
                          display: 'inline-block', padding: '2px 10px', borderRadius: 12,
                          background: statusColor + '18', color: statusColor,
                          fontSize: 12, fontWeight: 600,
                        }}>
                          {statusLabel}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                        <Group gap={4} wrap="nowrap">
                          <ActionIcon
                            size="sm" variant="subtle"
                            title={isExpanded ? 'Collapse' : 'Expand'}
                            onClick={() => setExpandedId(isExpanded ? null : pkg.id)}
                          >
                            {isExpanded ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
                          </ActionIcon>
                          {canEdit && (
                            <>
                              <ActionIcon size="sm" variant="subtle" title="Edit" onClick={() => openEditPW(pkg)}>
                                <IconPencil size={14} />
                              </ActionIcon>
                              <ActionIcon
                                size="sm" variant="subtle" color="red" title="Delete"
                                onClick={() => setDeleteTargetId(pkg.id)}
                              >
                                <IconTrash size={14} />
                              </ActionIcon>
                            </>
                          )}
                        </Group>
                      </td>
                    </tr>
                    {isExpanded && meta && (
                      <tr>
                        <td colSpan={8} style={{ background: '#f8fafc', padding: '16px 24px', borderBottom: '1px solid #e2e8f0' }}>
                          <SimpleGrid cols={2} spacing={24}>
                            {/* Readiness checklist */}
                            <div>
                              <Text size="sm" fw={600} mb={8}>Readiness Checklist</Text>
                              <Stack gap={6}>
                                {checks.map((c, i) => (
                                  <Group key={i} gap={8} align="flex-start">
                                    <span style={{
                                      flexShrink: 0,
                                      width: 18, height: 18, borderRadius: '50%',
                                      background: c.passed ? '#dcfce7' : '#fee2e2',
                                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                      {c.passed
                                        ? <IconCheck size={11} color="var(--color-success)" />
                                        : <IconX size={11} color="#ef4444" />}
                                    </span>
                                    <div>
                                      <Text size="xs" fw={500}>{c.label}</Text>
                                      <Text size="xs" c="dimmed">{c.note}</Text>
                                    </div>
                                  </Group>
                                ))}
                              </Stack>
                            </div>

                            <div>
                              {/* Linked Invoices */}
                              <Text size="sm" fw={600} mb={6}>Linked Invoices ({meta.linkedInvoiceIds.length})</Text>
                              {meta.linkedInvoiceIds.length === 0 ? (
                                <Text size="xs" c="dimmed">None linked</Text>
                              ) : (
                                <Stack gap={4} mb={12}>
                                  {meta.linkedInvoiceIds.map(iid => {
                                    const inv = invoiceMap.get(iid);
                                    return (
                                      <Group key={iid} gap={8}>
                                        <Text size="xs">#{invoices.find(i => i.id === iid)?.invoiceNumber ?? iid}</Text>
                                        <Text size="xs" c="dimmed">{inv ? formatCurrency(inv.amount) : '—'}</Text>
                                        {inv && (
                                          <span style={{
                                            fontSize: 10, padding: '1px 6px', borderRadius: 8,
                                            background: '#e8eaf0', color: '#374151',
                                          }}>
                                            {inv.status}
                                          </span>
                                        )}
                                      </Group>
                                    );
                                  })}
                                </Stack>
                              )}

                              {/* Linked Reconciliations */}
                              <Text size="sm" fw={600} mb={6}>Linked Reconciliations ({meta.linkedReconciliationIds.length})</Text>
                              {meta.linkedReconciliationIds.length === 0 ? (
                                <Text size="xs" c="dimmed">None linked</Text>
                              ) : (
                                <Stack gap={4}>
                                  {meta.linkedReconciliationIds.map(rid => {
                                    const rec = reconMap.get(rid);
                                    return (
                                      <Group key={rid} gap={8}>
                                        <Text size="xs">Recon #{rid}</Text>
                                        <Text size="xs" c="dimmed">{rec ? formatCurrency(rec.amount) : '—'}</Text>
                                        {rec && (
                                          <span style={{
                                            fontSize: 10, padding: '1px 6px', borderRadius: 8,
                                            background: '#e8eaf0', color: '#374151',
                                          }}>
                                            {rec.status}
                                          </span>
                                        )}
                                      </Group>
                                    );
                                  })}
                                </Stack>
                              )}

                              {/* Portal ref */}
                              {meta.portalRef && (
                                <div style={{ marginTop: 12 }}>
                                  <Text size="xs" fw={600}>PA Grants Portal Project #</Text>
                                  <Text size="xs" c="dimmed" style={{ fontFamily: 'monospace' }}>{meta.portalRef}</Text>
                                </div>
                              )}
                            </div>
                          </SimpleGrid>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </Paper>

      {/* ── Section 2: Submission Log ─────────────────────────── */}
      <Paper withBorder style={{ overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0' }}>
          <Group justify="space-between" align="center">
            <div>
              <Title order={4} style={{ margin: 0 }}>Submission Log</Title>
              <Text size="xs" c="dimmed" mt={2}>
                Each row is a versioned FEMA submission — track what was included in each package
              </Text>
            </div>
            {canEdit && (
              <Button
                leftSection={<IconPlus size={16} />}
                size="sm"
                variant="outline"
                onClick={openLogSubmission}
              >
                Log Submission
              </Button>
            )}
          </Group>
        </div>

        <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 400px)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle('version', subSortField)} onClick={() => toggleSubSort('version')}>
                  Version {sortIndicator('version', subSortField, subSortDir)}
                </th>
                <th style={thStyle('submittedDate', subSortField)} onClick={() => toggleSubSort('submittedDate')}>
                  Date Submitted {sortIndicator('submittedDate', subSortField, subSortDir)}
                </th>
                <th style={thStyle('submittedBy', subSortField)} onClick={() => toggleSubSort('submittedBy')}>
                  Submitted By {sortIndicator('submittedBy', subSortField, subSortDir)}
                </th>
                <th style={thStyle('pwCount', subSortField)} onClick={() => toggleSubSort('pwCount')}>
                  PWs Included {sortIndicator('pwCount', subSortField, subSortDir)}
                </th>
                <th style={thStyle('portalRef', subSortField)} onClick={() => toggleSubSort('portalRef')}>
                  PA Grants Portal Project # {sortIndicator('portalRef', subSortField, subSortDir)}
                </th>
                <th style={thStyle('changeNotes', subSortField)} onClick={() => toggleSubSort('changeNotes')}>
                  Notes {sortIndicator('changeNotes', subSortField, subSortDir)}
                </th>
                <th style={{ ...thStyle('', subSortField), cursor: 'default' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedSubs.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ ...tdStyle, textAlign: 'center', color: '#94a3b8', padding: '40px 20px' }}>
                    No submissions logged yet.
                  </td>
                </tr>
              )}
              {sortedSubs.map(sub => {
                const meta = decodeSubMeta(sub.notes ?? '');
                return (
                  <tr key={sub.id}>
                    <td style={{ ...tdStyle, fontWeight: 700, color: '#4B3FFF' }}>
                      v{meta?.version ?? sub.version}
                    </td>
                    <td style={tdStyle}>{meta?.submittedDate || '—'}</td>
                    <td style={{ ...tdStyle, color: '#64748b' }}>{meta?.submittedBy || '—'}</td>
                    <td style={tdStyle}>
                      {meta?.pwIds?.length
                        ? meta.pwIds.map(pid => {
                            const pw = pwRecords.find(p => p.id === pid);
                            return pw?.pwReference || `PW-${pid}`;
                          }).join(', ')
                        : <span style={{ color: '#94a3b8' }}>—</span>
                      }
                    </td>
                    <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 12 }}>
                      {meta?.portalRef || <span style={{ color: '#94a3b8' }}>—</span>}
                    </td>
                    <td style={{ ...tdStyle, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {meta?.changeNotes || <span style={{ color: '#94a3b8' }}>—</span>}
                    </td>
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                      <Group gap={4} wrap="nowrap">
                        <ActionIcon
                          size="sm" variant="subtle" title="View details"
                          onClick={() => setViewSubId(sub.id)}
                        >
                          <IconEye size={14} />
                        </ActionIcon>
                        {canEdit && (
                          <ActionIcon
                            size="sm" variant="subtle" color="red" title="Delete"
                            onClick={() => setDeleteSubId(sub.id)}
                          >
                            <IconTrash size={14} />
                          </ActionIcon>
                        )}
                      </Group>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Paper>

      {/* ── Add/Edit PW Modal ─────────────────────────────────── */}
      <Modal
        opened={pwModalOpen}
        onClose={() => setPwModalOpen(false)}
        title={editTargetId != null ? 'Edit Project Worksheet' : 'Add Project Worksheet'}
        size="lg"
      >
        <Stack gap={14}>
          <SimpleGrid cols={2} spacing={12}>
            <TextInput
              label="PW Number"
              placeholder="e.g. PW-0001"
              required
              value={pwForm.pwNumber}
              onChange={e => setPwForm(f => ({ ...f, pwNumber: e.target.value }))}
            />
            <Select
              label="Work Category"
              data={[
                { value: 'Cat A', label: 'Cat A — Debris Removal' },
                { value: 'Cat B', label: 'Cat B — Emergency Protective' },
                { value: 'Cat Z', label: 'Cat Z — Management Costs' },
              ]}
              value={pwForm.workCategory}
              onChange={v => setPwForm(f => ({ ...f, workCategory: (v ?? 'Cat A') as PWForm['workCategory'] }))}
            />
          </SimpleGrid>

          <Textarea
            label="Description"
            placeholder="Brief description of work covered by this PW"
            rows={3}
            value={pwForm.description}
            onChange={e => setPwForm(f => ({ ...f, description: e.target.value }))}
          />

          <SimpleGrid cols={2} spacing={12}>
            <TextInput
              label="Estimated Amount"
              placeholder="e.g. 1,250,000"
              value={fmtInputNum(pwForm.estimatedAmount)}
              onChange={e => setPwForm(f => ({ ...f, estimatedAmount: stripNumFmt(e.target.value) }))}
              leftSection={<span style={{ color: '#64748b', fontSize: 13 }}>$</span>}
            />
            <TextInput
              label="Obligated Amount — optional"
              placeholder="e.g. 1,100,000"
              value={fmtInputNum(pwForm.obligatedAmount)}
              onChange={e => setPwForm(f => ({ ...f, obligatedAmount: stripNumFmt(e.target.value) }))}
              leftSection={<span style={{ color: '#64748b', fontSize: 13 }}>$</span>}
            />
          </SimpleGrid>

          <MultiSelect
            label="Link Invoices"
            placeholder="Select invoices to link"
            data={invoiceOptions}
            value={pwForm.linkedInvoiceIds}
            onChange={v => setPwForm(f => ({ ...f, linkedInvoiceIds: v }))}
            searchable
            clearable
          />

          <MultiSelect
            label="Link Reconciliations"
            placeholder="Select reconciliations to link"
            data={reconOptions}
            value={pwForm.linkedReconciliationIds}
            onChange={v => setPwForm(f => ({ ...f, linkedReconciliationIds: v }))}
            searchable
            clearable
          />

          <SimpleGrid cols={2} spacing={12}>
            <TextInput
              label="PA Grants Portal Project #"
              placeholder="e.g. PA-DR4834-FL-2025-PW001"
              value={pwForm.portalRef}
              onChange={e => setPwForm(f => ({ ...f, portalRef: e.target.value }))}
            />
            <Select
              label="Status"
              data={[
                { value: 'Formulating', label: 'Formulating' },
                { value: 'Ready', label: 'Ready' },
                { value: 'Submitted', label: 'Submitted' },
                { value: 'Obligated', label: 'Obligated' },
                { value: 'Closed', label: 'Closed' },
              ]}
              value={pwForm.status}
              onChange={v => setPwForm(f => ({ ...f, status: v ?? 'Formulating' }))}
            />
          </SimpleGrid>

          <Group justify="flex-end" gap={10} mt={8}>
            <Button variant="subtle" onClick={() => setPwModalOpen(false)} disabled={pwSaving}>Cancel</Button>
            <Button onClick={savePW} loading={pwSaving}>
              {editTargetId != null ? 'Save Changes' : 'Add PW'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Delete PW confirm ─────────────────────────────────── */}
      <Modal
        opened={deleteTargetId != null}
        onClose={() => setDeleteTargetId(null)}
        title="Delete Project Worksheet"
        size="sm"
      >
        <Text size="sm" mb={20}>
          Are you sure you want to delete this Project Worksheet? This cannot be undone.
        </Text>
        <Group justify="flex-end" gap={10}>
          <Button variant="subtle" onClick={() => setDeleteTargetId(null)} disabled={deleting}>Cancel</Button>
          <Button color="red" onClick={deletePW} loading={deleting}>Delete</Button>
        </Group>
      </Modal>

      {/* ── Log Submission Modal ──────────────────────────────── */}
      <Modal
        opened={subModalOpen}
        onClose={() => setSubModalOpen(false)}
        title="Log FEMA Submission"
        size="lg"
      >
        <Stack gap={14}>
          <MultiSelect
            label="PWs Included in this Submission"
            placeholder="Select Project Worksheets"
            data={pwOptions}
            value={subForm.selectedPwIds}
            onChange={v => setSubForm(f => ({ ...f, selectedPwIds: v }))}
            searchable
            clearable
            required
          />

          <SimpleGrid cols={2} spacing={12}>
            <TextInput
              label="Submission Date"
              type="date"
              value={subForm.submittedDate}
              onChange={e => setSubForm(f => ({ ...f, submittedDate: e.target.value }))}
            />
            <TextInput
              label="Submitted By"
              placeholder="Name or email"
              value={subForm.submittedBy}
              onChange={e => setSubForm(f => ({ ...f, submittedBy: e.target.value }))}
            />
          </SimpleGrid>

          <TextInput
            label="PA Grants Portal Project #"
            placeholder="e.g. PA-DR4834-FL-2025-SUB-001"
            value={subForm.portalRef}
            onChange={e => setSubForm(f => ({ ...f, portalRef: e.target.value }))}
          />

          <Textarea
            label="Change Notes"
            placeholder="e.g. Added PW-0061, supplemented PW-0023 with additional invoices"
            rows={3}
            value={subForm.changeNotes}
            onChange={e => setSubForm(f => ({ ...f, changeNotes: e.target.value }))}
          />

          <Group justify="flex-end" gap={10} mt={8}>
            <Button variant="subtle" onClick={() => setSubModalOpen(false)} disabled={subSaving}>Cancel</Button>
            <Button onClick={saveSubmission} loading={subSaving}>Log Submission</Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── View Submission Detail Modal ──────────────────────── */}
      <Modal
        opened={viewSubId != null}
        onClose={() => setViewSubId(null)}
        title={`Submission v${viewedSubMeta?.version ?? ''} Details`}
        size="md"
      >
        {viewedSubMeta && (
          <Stack gap={12}>
            <SimpleGrid cols={2} spacing={12}>
              <div>
                <Text size="xs" c="dimmed">Date Submitted</Text>
                <Text size="sm" fw={600}>{viewedSubMeta.submittedDate || '—'}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed">Submitted By</Text>
                <Text size="sm" fw={600}>{viewedSubMeta.submittedBy || '—'}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed">PA Grants Portal Project #</Text>
                <Text size="sm" fw={600} style={{ fontFamily: 'monospace' }}>{viewedSubMeta.portalRef || '—'}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed">PWs Included</Text>
                <Text size="sm" fw={600}>{viewedSubMeta.pwIds?.length ?? 0}</Text>
              </div>
            </SimpleGrid>

            {viewedSubMeta.pwIds?.length > 0 && (
              <div>
                <Text size="sm" fw={600} mb={6}>Included Project Worksheets</Text>
                <Stack gap={4}>
                  {viewedSubMeta.pwIds.map(pid => {
                    const pw = pwRecords.find(p => p.id === pid);
                    return (
                      <Group key={pid} gap={8}>
                        <Text size="sm" fw={600} style={{ color: '#4B3FFF' }}>
                          {pw?.pwReference || `PW-${pid}`}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {decodePWMeta(pw?.notes ?? '')?.description?.slice(0, 60) ?? ''}
                        </Text>
                      </Group>
                    );
                  })}
                </Stack>
              </div>
            )}

            {viewedSubMeta.changeNotes && (
              <div>
                <Text size="xs" c="dimmed" mb={4}>Change Notes</Text>
                <Text size="sm">{viewedSubMeta.changeNotes}</Text>
              </div>
            )}

            <Group justify="flex-end" mt={8}>
              <Button variant="subtle" onClick={() => setViewSubId(null)}>Close</Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* ── Delete Submission Confirm ──────────────────────────── */}
      <Modal
        opened={deleteSubId != null}
        onClose={() => setDeleteSubId(null)}
        title="Delete Submission"
        size="sm"
      >
        <Text size="sm" mb={20}>
          Are you sure you want to delete this submission record? This cannot be undone.
        </Text>
        <Group justify="flex-end" gap={10}>
          <Button variant="subtle" onClick={() => setDeleteSubId(null)} disabled={deletingSubId}>Cancel</Button>
          <Button color="red" onClick={deleteSubmission} loading={deletingSubId}>Delete</Button>
        </Group>
      </Modal>
    </div>
  );
}
